import { QueryHookOptions } from '@apollo/client';
import React from 'react';
import { OperationData, OperationVariables, gql } from '@ts-gql/tag';
import { useQuery } from '@ts-gql/apollo';

import { TaskStatusEnum } from '../components/work/types/task-status-enum';
import { getTaskStatus } from '../globals';
import { parseSearchTasksVariables } from '../utils/data-utils';
import {
  useTenantTimezoneConversion,
  utcToTenantToLocalDateFunction,
} from '../hooks/use-tenant-timezone';

// TODO: find out what is needed exactly in the return and remove any that is unnecessary.
export const SEARCH_TASKS_WITH_JOB_QUERY = gql`
  query SearchTasksWithJob(
    $clients: [String!] = []
    $entities: [String!] = []
    $jobs: [String!] = []
    $myWork: Boolean! = false
    $dueDateOverdue: Boolean = false
    $staff: [String!] = []
    $statuses: [Float!] = []
    $dueDateBegin: DateTime = null
    $dueDateEnd: DateTime = null
    $completedDateBegin: DateTime = null
    $completedDateEnd: DateTime = null
    $familyGroupIds: [String!] = []
    $text: String = null
    $take: Float = null
    $excludeClosedJobTasks: Boolean = null
  ) {
    searchTasks(
      clients: $clients
      entities: $entities
      jobs: $jobs
      myWork: $myWork
      staff: $staff
      statuses: $statuses
      dueDateBegin: $dueDateBegin
      dueDateEnd: $dueDateEnd
      dueDateOverdue: $dueDateOverdue
      completedDateBegin: $completedDateBegin
      completedDateEnd: $completedDateEnd
      familyGroupIds: $familyGroupIds
      text: $text
      take: $take
      excludeClosedJobTasks: $excludeClosedJobTasks
    ) {
      ...SearchTasksWithJobFragment_Task
    }
  }
` as import('../../__generated__/ts-gql/SearchTasksWithJob').type;

export const SearchTasksWithJobFragment = gql`
  fragment SearchTasksWithJobFragment_Task on Task {
    cloudId
    jobId
    assignedToUserId
    dueDate
    completedDate
    entityId
    name
    status
    taskTypeId
    estimateMinutes
    job {
      cloudId
      name
      jobStatusId
      clientId
      WfDeletedOnUTC
    }
  }
` as import('../../__generated__/ts-gql/SearchTasksWithJobFragment_Task').type;

export type SearchTasksWithJobQueryVariables = OperationVariables<
  typeof SEARCH_TASKS_WITH_JOB_QUERY
>;
export type SearchTasksWithJobQueryResult = OperationData<
  typeof SEARCH_TASKS_WITH_JOB_QUERY
>;

export type SearchTasksWithJobResult = NonNullable<
  ReturnType<typeof processData>
>[number];

export const useSearchTasksWithJob = (
  options: Partial<
    QueryHookOptions<
      SearchTasksWithJobQueryResult,
      SearchTasksWithJobQueryVariables
    >
  >
) => {
  const variables = options.variables as SearchTasksWithJobQueryVariables;

  const { localDateToTenantToUtc, utcToTenantToLocalDate, loading } =
    useTenantTimezoneConversion();

  const res = useQuery(SEARCH_TASKS_WITH_JOB_QUERY, {
    context: { service: 'core' },
    ...options,
    variables: parseSearchTasksVariables(variables, localDateToTenantToUtc),
    skip: options.skip || loading,
  });

  return React.useMemo(() => {
    if (loading) {
      return {
        loading: true,
        data: undefined,
        error: undefined,
        refetch: undefined,
      };
    }

    return {
      ...res,
      data: processData(res.data, utcToTenantToLocalDate),
    };
  }, [res, utcToTenantToLocalDate, loading]);
};

export function processData(
  data: SearchTasksWithJobQueryResult | undefined,
  dateConvert: utcToTenantToLocalDateFunction
) {
  const raw = data?.searchTasks;
  const results =
    raw &&
    raw.map((task) => ({
      ...task,
      // the ternary for status is to please the compiler. Tasks MUST have statuses, and we've retrieved the status
      status: task.status
        ? getTaskStatus(task.status)
        : TaskStatusEnum.valuesEnum.NotStarted,
      statusOrderIndex: task.status || 1,
      dueDate: task.dueDate && dateConvert(task.dueDate),
      completedDate: task.completedDate && new Date(task.completedDate),
      // wfDeletedOnUTC: task.wfDeletedOnUTC && new Date(task.wfDeletedOnUTC),
      // TO DO: Used To be the above but it doesn't exist there??? Have changed below, will need to be tested
      wfDeletedOnUTC:
        task.job?.WfDeletedOnUTC && new Date(task.job?.WfDeletedOnUTC),
    }));
  return results;
}
